import { connect } from 'react-redux';
import * as R from 'ramda';

import { TAgentRating } from '../../../api/agent';
import { Dispatch, IAppState, ICommercialTrackingOfferParams } from '../../../common/state';
import { changeFavorite } from '../../../filters/state/favorite';
import { IKpOffersByCategoryCounter, IOffer } from '../../../offer';
import { isGoogleBot, isYandexBot } from '../../../utils/user_agent';
import { hideMotivationPopup } from '../../state/login_motivation_popup';
import { updateAgentRating } from '../../state/offer_card/agent';
import { changeOfferComments } from '../../state/offer_card/comments';
import { addOfferComplaint } from '../../state/offer_card/complaints';
import {
  simplifiedOfferCardPopupCloseAction,
  simplifiedOfferCardPopupOpenAction,
} from '../../state/offer_card/simplified_card_popups';
import { IOffersDispatchProps, IOffersStateProps, Offers } from './index';
import { hideOfferAction, IHidingOfferInfo } from '../../state/hide_offer';
import {
  EFeedbackComplaint,
  IComplaintFeedbackBody,
  sendFeedbackComplaint,
  statusChanger,
} from '../../state/offer_card/feedback_complaint';
import { isInSerpLightGroup } from '../../../utils/experiments/audienceSerpLight';
import { isSuburban, offerTypeFromJsonQuery } from '../../../utils/category';
import { isExperimentActive } from '../../../api/experiments';
import { openCommercialWidgetModal } from '../../state/commecial_survey';
import { isNewbuildingConsultBannerAvailable } from '../../../selectors/newbuildingConsultBannerAvailable';
import { isDealRentCianAgency } from '../../../utils/dealRentCianAgencyExperiment';
import { isRedesignExperimentEnabled } from '../../../selectors/isRedesignExperimentEnabled';
import { prepareTrackingData } from '../../../utils/prepareTrackingData';
import { onOfferClick } from '../../state/coworking_experiments/coworkingAdModal';

/* istanbul ignore next */
export const mapStateToProps = (state: IAppState): IOffersStateProps => {
  const {
    config,
    currentPath,
    currentSubdomain,
    experiments,
    logger,
    loginMotivationPopupOnFavorite,
    makeRequest,
    print,
    results,
    simplifiedOfferCardPopupsState,
    suggestionDistancesSeoText,
    userAgent,
    userGADataLayerData,
    user,
    showPushNotification,
    hideOffer,
    complaintFeedbackFrom,
    maxAuctionBet,
    maxAuctionService,
    deviceType,
    soprApi,
    villagesLink,
    isCountrysideTrapEnabled,
    isPromotionsInOffersEnabled,
    isQaMode,
    excludedUtilitiesTermsRegions,
    featureToggle,
    filters,
    breadcrumbs,
    auctionBanks,
    abUseExperiments,
  } = state;

  const {
    commentingBlockedOffers,
    commentingBlockedAgents,
    commentingErroneousOffers,
    commentingErroneousAgents,
    jsonQuery,
    kp,
    offers,
    qsToUris,
    ratingBlockedAgents,
    aggregatedOffers,
    extendedOffersCount,
  } = results;
  const emptyKpOffersCounter = (kp && kp.offers_by_category_counter) || {};
  const hasKpEmptyResults =
    Object.keys(emptyKpOffersCounter).filter((key: keyof IKpOffersByCategoryCounter) =>
      Number(emptyKpOffersCounter[key]),
    ).length > 0;
  const showCountrysideTrap = isCountrysideTrapEnabled && isSuburban(offerTypeFromJsonQuery(jsonQuery));
  const isCountrysidePromotionsExperimentActive = isExperimentActive(experiments, 'countryside_promotions');
  const isOfferInSavedSearch = R.pathOr(undefined, ['results', 'jsonQuery', 'saved_search_id'], state);
  const isPopupsOpened = () => {
    const popupsStateKeys = Object.keys(simplifiedOfferCardPopupsState).map(Number);

    return popupsStateKeys.some(offerId => Boolean(simplifiedOfferCardPopupsState[offerId].length));
  };

  return {
    queryString: results.queryString,
    offersQty: results.totalOffers,
    baseUrl: config.get('apiBaseUrl') || '',
    breadcrumbs,
    commentingBlockedOffers,
    commentingBlockedAgents,
    commentingErroneousOffers,
    commentingErroneousAgents,
    currentPageNumber: jsonQuery.page ? jsonQuery.page.value : 1,
    currentPath,
    currentSubdomain,
    experiments,
    isBot: isGoogleBot(userAgent) || isYandexBot(userAgent),
    isPopupsOpened,
    isPrintEnabled: print.enabled,
    jsonQuery,
    logger,
    makeRequest,
    offers,
    hideOffer,
    offersPerPage: Number(config.get('serp.offersPerPage')),
    qsToUris,
    ratingBlockedAgents,
    showEmptyKpSearch: hasKpEmptyResults,
    shownId: loginMotivationPopupOnFavorite.shownId,
    suggestionDistancesSeoText,
    user,
    userGADataLayerData,
    maxAuctionBet,
    maxAuctionService,
    mlRankingGuid: state.mlRankingGuid,
    isPromotionsInOffersActive: Boolean(isPromotionsInOffersEnabled && isCountrysidePromotionsExperimentActive),
    isOfferInSavedSearch,

    // лимит избранного для неавторизованного юзера
    favoritesLimitForUnauthUser: 5,

    showPushNotification,

    complaintsFormStatus: complaintFeedbackFrom.status,
    isLightView: isInSerpLightGroup(state),
    soprApi,
    deviceType,
    villagesLink,
    showCountrysideTrap,

    // количество всех и расширенных офферов, используем для вычисления позиции в выдаче
    aggregatedOffersCount: aggregatedOffers,
    extendedOffersCount,
    isQaMode,
    excludedUtilitiesTermsRegions,
    isRedesignEnabled: isRedesignExperimentEnabled(state),
    newbuildingConsultBannerAvailable: isNewbuildingConsultBannerAvailable(state),
    isCianAgencyExperimentActive: isDealRentCianAgency(state.abUseExperiments),
    featureToggle,
    trackingData: prepareTrackingData(filters, results, breadcrumbs, userGADataLayerData),
    auctionBanks,
    abUseExperiments,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch): IOffersDispatchProps => {
  return {
    closePopup: () => {
      dispatch(hideMotivationPopup());
    },
    onAgentRatingChanged: (offer: IOffer, rating: TAgentRating) => {
      dispatch(updateAgentRating(offer, rating));
    },
    onComplaintSent: (offerId: number, name: string, message?: string) => {
      dispatch(addOfferComplaint(offerId, name, message));
    },
    onFavoriteChanged: (offer: IOffer, isFavorite: boolean) => {
      dispatch(changeFavorite(offer, isFavorite));
    },
    onOfferCommentsChanged: (offer: IOffer, commentOffer: string | undefined, commentAgent: string | undefined) => {
      dispatch(changeOfferComments(offer, commentOffer, commentAgent));
    },
    onPopupMoreOpen: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'more'));
    },
    onPopupReportOpen: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'report'));
    },
    onPopupMoreClose: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'more'));
    },
    onPopupReportClose: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'report'));
    },
    onUserInfoPopupOpen: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'agent'));
    },
    onUserInfoPopupClose: (offerId: number) => {
      dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'agent'));
    },
    hideOfferAction: (hidingOffer: IHidingOfferInfo) => {
      dispatch(hideOfferAction(hidingOffer));
    },
    sendComplaintFeedback: (body: IComplaintFeedbackBody) => {
      dispatch(sendFeedbackComplaint(body));
    },
    statusChanger: (status: EFeedbackComplaint) => {
      dispatch(statusChanger(status));
    },
    onAnalyticsButtonClick: (offerId: number, params: ICommercialTrackingOfferParams) => {
      dispatch(openCommercialWidgetModal(offerId, params));
    },
    onOfferCardClick: () => {
      dispatch(onOfferClick());
    },
  };
};

export const OffersContainer = connect<IOffersStateProps, IOffersDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(Offers);
